import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import i18n from '../../../Config/app/i18n';
import { PostHogEventEnum, PostHogFeatureFlagEnum } from '../../enum/PostHogEvent.enum';
import errorMapper, { Error, ErrorCodes } from '../../utils/errorMapper';
import { useCaptchaFallback } from './useCaptchaFallback.hook';
import { QueryKey } from '@/Config/api/queryKey';
import { setPendingVerificationEmail } from '@/Domain/utils/pendingVerificationEmail';

export const PASSWORD_MIN_LENGTH = 14;

const DEFAULT_FORM_VALUES = {
  username: '',
  password1: '',
  password2: '',
  tos_consent: true,
  pp_consent: true,
  recaptcha_response_v3: '',
};

const RegisterValidator = z
  .object({
    username: z.string().email(),
    password1: z.string().min(PASSWORD_MIN_LENGTH, i18n.t('validation:password_min_length')),
    password2: z.string().min(1, i18n.t('validation:password_required')),
    tos_consent: z.boolean(),
    pp_consent: z.boolean(),
    recaptcha_response_v3: z.string().optional(),
    recaptcha_response_v2: z.string().optional(),
  })
  .refine((data) => data.password1 === data.password2, {
    message: i18n.t('validation:match_password'),
    path: ['password2'],
  });

export type RegisterForm = z.infer<typeof RegisterValidator>;

export const useRegister = () => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const postHog = usePostHog();
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({
    mode: 'onTouched',
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(RegisterValidator),
  });

  const {
    handleCaptchaError,
    handleSubmissionError,
    handleV2Focus,
    prepareCaptchaSubmission,
    shouldFocusV2,
    useV2,
  } = useCaptchaFallback({
    clearSubmissionErrors: () => {
      clearErrors('root');
      clearErrors('recaptcha_response_v2');
    },
    hasV2Token: () => !!getValues('recaptcha_response_v2'),
    resetV2Token: () => setValue('recaptcha_response_v2', ''),
    setGenericError: () => setError('root', { message: i18n.t('register:error_generic') }),
    setRequiredV2Error: () => setError('recaptcha_response_v2', { type: 'required' }),
  });

  const mutation = useMutation<{ email: string }, Error<RegisterForm>, RegisterForm>({
    mutationKey: [QueryKey.accounts.register],
    onSuccess: (response) => {
      const { email } = response;

      // Get the A/B test variant for tracking
      const backgroundVariant = postHog.getFeatureFlag(PostHogFeatureFlagEnum.SignupBackgroundTest);

      // Capture in PostHog for experiment analysis (before navigation)
      postHog.capture(PostHogEventEnum.SignupCompleted, {
        background_variant: backgroundVariant,
      });

      track('register_form_success', {
        event_category: 'User Registration',
        event_label: 'register_form',
        background_variant: backgroundVariant ?? 'unknown',
      });

      setPendingVerificationEmail(email);

      // `account_created` GTM event fires from main.tsx on the `?account_activated=1` redirect after email verification.
      navigate('/register/verify', { viewTransition: true });
    },
    onError: (error) => {
      handleSubmissionError();
      track('register_form_error', {
        event_category: 'User Registration',
        event_label: 'register_form',
      });

      if ((error.response?.status ?? 0) >= 500) {
        const message = i18n.t('register:error_generic');
        setError('root', { message });
        toast.error(message);
        return;
      }

      const fieldErrors = error.response?.data?.errors;
      let bannerMessage: string | undefined;
      let handled = false;

      errorMapper(fieldErrors || {}, (field, message, code) => {
        if (handleCaptchaError(field)) {
          if (field === 'recaptcha_response_v2') {
            toast.error(i18n.t('register:error_generic'));
          }
          handled = true;
          return;
        }
        if (code === ErrorCodes.TOO_MANY_REQUESTS) {
          bannerMessage = i18n.t('ERRORS:TOO_MANY_REQUESTS');
          return;
        }
        if (field === 'username' || field === 'password1' || field === 'password2') {
          setError(field, { message });
          handled = true;
          return;
        }
        // root / unknown field / other coded errors have no inline slot -> surface as a banner
        bannerMessage = message || bannerMessage;
      });

      if (!bannerMessage && !handled) {
        bannerMessage = i18n.t('register:error_generic');
      }

      if (bannerMessage) {
        setError('root', { message: bannerMessage });
        toast.error(bannerMessage);
      }
    },
  });

  const onSubmit = (data: RegisterForm) => {
    const payload = prepareCaptchaSubmission(data);
    if (!payload) {
      return;
    }

    track('register_form_submit', {
      event_category: 'User Registration',
      event_label: 'register_form',
    });

    mutation.mutate(payload);
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [mutation.submittedAt],
  );

  register('tos_consent');
  register('pp_consent');
  register('recaptcha_response_v3');

  return {
    control,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    register,
    handleSubmit,
    onSubmit,
    errors,
    onVerify,
    watch,
    isPending: mutation.isPending,
  };
};
